import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { usePermissions } from '../context/PermissionContext';
import AdminLayout from '../components/admin/shared/AdminLayout';
import OverviewTab from '../components/admin/Overview/OverviewTab';
import WelcomeDashboard from '../components/admin/WelcomeDashboard';
import TrialStatusBanner from '../components/admin/TrialStatusBanner';
import MembersTab from '../components/admin/Members/MembersTab';
import PlansTab from '../components/admin/Plans/PlansTab';
import FeesTab from '../components/admin/Fees/FeesTab';
import AppointmentsTab from '../components/admin/Appointments/AppointmentsTab';
import PaymentsTab from '../components/admin/Payments/PaymentsTab';
import AuditLogsTab from '../components/admin/AuditLogs/AuditLogsTab';
import WordPressTab from '../components/admin/WordPress/WordPressTab';
import StripeConnectTab from '../components/admin/StripeConnect/StripeConnectTab';
import MessagesTab from '../components/admin/Messages/MessagesTab';
import AnalyticsTab from '../components/admin/Analytics/AnalyticsTab';
import ReportsTab from '../components/admin/ReportsTab';
import EnhancedReportsTab from '../components/admin/EnhancedReportsTab';
import BeforeAfterGallery from '../components/admin/BeforeAfterGallery';
import TreatmentPackages from '../components/admin/TreatmentPackages';
import Consultations from '../components/admin/Consultations';
import MembershipTemplates from '../components/admin/MembershipTemplates';
import AIAnalyticsDashboard from '../components/admin/AI/AIAnalyticsDashboard';
import SubscriptionView from '../components/admin/SubscriptionView';
import HelpCenter from '../components/help/HelpCenter';
import OnboardingTour from '../components/help/OnboardingTour';
import HelpWidget from '../components/help/HelpWidget';
import ClinicSettingsPage from '../components/admin/Settings/ClinicSettingsPage';
import OnboardingWizard from '../components/admin/OnboardingWizard';
import DemoContentModal from '../components/admin/DemoContentModal';
import TeamManagement from '../components/admin/Team/TeamManagement';
import RoleManagement from '../components/admin/Settings/RoleManagement';
import ServicesManagement from '../components/admin/Services/ServicesManagement';
import ProvidersManagement from '../components/admin/Services/ProvidersManagement';
import BenefitsManagement from '../components/admin/Benefits/BenefitsManagement';
import FulfillmentDashboard from '../components/admin/FulfillmentDashboard';
import BrandingPage from '../components/admin/Branding/BrandingPage';
import PaymentGatewaysSettings from '../components/admin/Settings/PaymentGatewaysSettings';
import { MarketingDashboard, ReferralProgram, PromoCodes } from '../components/admin/Marketing';
import { LoyaltyManagement } from '../components/admin/Loyalty';

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canView, isAdmin, loading: permissionsLoading } = usePermissions();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [clinic, setClinic] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);

  // Redirect users without dashboard permission to members page
  useEffect(() => {
    if (!permissionsLoading && !isAdmin && !canView('dashboard') && location.pathname === '/admin') {
      navigate('/admin/members', { replace: true });
    }
  }, [permissionsLoading, isAdmin, canView, location.pathname, navigate]);

  // Fetch data only after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && user) {
      // Small delay to ensure token and permissions are fully set
      const timeoutId = setTimeout(() => {
        fetchDashboardData();
        checkOnboardingStatus();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [permissionsLoading, user, isAdmin]);

  const checkOnboardingStatus = async () => {
    // First check localStorage for immediate response
    const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
    const hasCompletedByEmail = localStorage.getItem(`onboarding_completed_${user.email}`);
    
    if (hasCompletedOnboarding || hasCompletedByEmail) {
      // Already completed locally
      checkDemoContentStatus();
      return;
    }
    
    // Check backend for persistent state
    try {
      const response = await axios.get(`${API}/auth/user/onboarding-status`);
      if (response.data?.onboarding_completed) {
        // Sync localStorage with backend
        localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
        localStorage.setItem(`onboarding_completed_${user.email}`, 'true');
        checkDemoContentStatus();
        return;
      }
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
    }
    
    // Show onboarding if not completed
    setTimeout(() => {
      setShowOnboarding(true);
    }, 1000);
  };

  const checkDemoContentStatus = () => {
    checkDemoContentStatusWithData(plans, members);
  };
  
  const checkDemoContentStatusWithData = (plansData, membersData) => {
    // Only show demo modal if:
    // 1. User has completed onboarding
    // 2. Clinic has no data (no plans, no members)
    // 3. User hasn't dismissed the modal before
    const hasDismissedDemo = localStorage.getItem(`demo_modal_dismissed_${user.clinic_id}`);
    
    if (!hasDismissedDemo && plansData.length === 0 && membersData.length === 0) {
      setTimeout(() => {
        setShowDemoModal(true);
      }, 2000);
    }
  };

  const handleDemoModalClose = () => {
    setShowDemoModal(false);
    // Remember user's choice
    localStorage.setItem(`demo_modal_dismissed_${user.clinic_id}`, 'true');
  };

  const handleOnboardingComplete = async () => {
    // Save to localStorage immediately
    localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
    localStorage.setItem(`onboarding_completed_${user.email}`, 'true');
    setShowOnboarding(false);
    toast.success('Welcome to MyMedMember by Clinicax Systems! 🎉');
    
    // Also save to backend for persistence
    try {
      await axios.post(`${API}/auth/user/complete-onboarding`);
    } catch (error) {
      console.error('Failed to save onboarding status:', error);
    }
    
    // After onboarding, check if should show demo modal
    setTimeout(() => {
      checkDemoContentStatus();
    }, 1000);
  };

  const fetchDashboardData = async (retryCount = 0) => {
    // Ensure token exists before making API calls
    const token = localStorage.getItem('token');
    if (!token) {
      if (retryCount < 3) {
        console.log('No token found, retrying dashboard fetch in 500ms...');
        setTimeout(() => fetchDashboardData(retryCount + 1), 500);
        return;
      }
      console.error('No token available after retries');
      setLoading(false);
      return;
    }
    
    try {
      // Fetch data based on permissions - only fetch what user can access
      const promises = [
        axios.get(`${API}/members`).catch(() => ({ data: [] })),
        axios.get(`${API}/plans`).catch(() => ({ data: [] })),
        axios.get(`${API}/appointments`).catch(() => ({ data: [] })),
        axios.get(`${API}/payments`).catch(() => ({ data: [] })),
        axios.get(`${API}/clinic/info`).catch(() => ({ data: null }))
      ];
      
      // Only fetch dashboard stats and audit logs if user has permission
      const shouldFetchDashboard = isAdmin || canView('dashboard');
      const shouldFetchAuditLogs = isAdmin || canView('settings');
      
      if (shouldFetchDashboard) {
        promises.push(axios.get(`${API}/dashboard/stats`).catch(() => ({ data: null })));
      }
      if (shouldFetchAuditLogs) {
        promises.push(axios.get(`${API}/audit-logs`).catch(() => ({ data: [] })));
      }

      const results = await Promise.all(promises);
      
      const [membersRes, plansRes, appointmentsRes, paymentsRes, clinicRes] = results;
      const statsRes = shouldFetchDashboard ? results[5] : { data: null };
      const logsRes = shouldFetchAuditLogs ? results[shouldFetchDashboard ? 6 : 5] : { data: [] };

      const fetchedPlans = plansRes.data || [];
      const fetchedMembers = membersRes.data || [];
      
      setStats(statsRes.data);
      setMembers(fetchedMembers);
      setPlans(fetchedPlans);
      setAppointments(appointmentsRes.data || []);
      setPayments(paymentsRes.data || []);
      setAuditLogs(logsRes.data || []);
      setClinic(clinicRes.data);
      
      // Check if should show welcome dashboard (first login after registration)
      const isFirstLogin = localStorage.getItem(`first_login_${user.id}`);
      if (isFirstLogin === 'true') {
        setShowWelcome(true);
        localStorage.removeItem(`first_login_${user.id}`);
      }
      
      // After data is fetched, check if demo modal should be shown
      // Only if onboarding is already completed and user is admin
      if (isAdmin) {
        const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user.id}`);
        if (hasCompletedOnboarding) {
          checkDemoContentStatusWithData(fetchedPlans, fetchedMembers);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Don't show error toast for permission-related issues
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <>
      <AdminLayout user={user} onLogout={handleLogout}>
        {/* Trial Status Banner */}
        {clinic && <TrialStatusBanner clinic={clinic} />}
        
        <Routes>
          <Route index element={
            showWelcome ? 
              <WelcomeDashboard user={user} clinic={clinic} /> : 
              <OverviewTab stats={stats} />
          } />
          <Route path="welcome" element={<WelcomeDashboard user={user} clinic={clinic} />} />
          <Route path="members" element={<MembersTab members={members} fetchData={fetchDashboardData} />} />
          <Route path="plans" element={<PlansTab plans={plans} fetchData={fetchDashboardData} />} />
          <Route path="fees" element={<FeesTab />} />
          <Route path="appointments" element={<AppointmentsTab appointments={appointments} members={members} fetchData={fetchDashboardData} />} />
          <Route path="payments" element={<PaymentsTab payments={payments} members={members} fetchData={fetchDashboardData} />} />
          <Route path="messages" element={<MessagesTab />} />
          <Route path="analytics" element={<AnalyticsTab />} />
          <Route path="reports" element={<EnhancedReportsTab />} />
          <Route path="before-after" element={<BeforeAfterGallery />} />
          <Route path="packages" element={<TreatmentPackages />} />
          <Route path="consultations" element={<Consultations />} />
          <Route path="templates" element={<MembershipTemplates />} />
          <Route path="ai-analytics" element={<AIAnalyticsDashboard />} />
          <Route path="subscription" element={<SubscriptionView />} />
          <Route path="stripe-connect" element={<StripeConnectTab />} />
          <Route path="wordpress" element={<WordPressTab />} />
          <Route path="audit" element={<AuditLogsTab logs={auditLogs} />} />
          <Route path="team" element={<TeamManagement user={user} />} />
          <Route path="roles" element={<RoleManagement />} />
          <Route path="settings" element={<ClinicSettingsPage user={user} />} />
          <Route path="services" element={<ServicesManagement />} />
          <Route path="providers" element={<ProvidersManagement />} />
          <Route path="benefits" element={<BenefitsManagement />} />
          <Route path="fulfillment" element={<FulfillmentDashboard />} />
          <Route path="branding" element={<BrandingPage />} />
          <Route path="payment-gateways" element={<PaymentGatewaysSettings />} />
          <Route path="marketing" element={<MarketingDashboard />} />
          <Route path="referrals" element={<ReferralProgram />} />
          <Route path="promotions" element={<PromoCodes />} />
          <Route path="loyalty" element={<LoyaltyManagement />} />
        </Routes>
      </AdminLayout>

      {/* Onboarding Wizard */}
      <OnboardingWizard
        user={user}
        open={showOnboarding}
        onComplete={handleOnboardingComplete}
      />

      {/* Demo Content Modal */}
      <DemoContentModal
        open={showDemoModal}
        onClose={handleDemoModalClose}
        onSuccess={() => {
          setShowDemoModal(false);
          toast.success('Demo content loaded! Refreshing dashboard...');
        }}
      />

      {/* Onboarding Tour */}
      <OnboardingTour role="admin" />

      {/* Help Widget */}
      <HelpWidget />
    </>
  );
};

export default AdminDashboard;
